import { Avatar as MantineAvatar, Box, Tooltip, useMantineTheme } from '@mantine/core';
import { forwardRef } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface AvatarItem {
  src?: string;
  name: string;
  color?: string;
}

export interface AvatarGroupProps {
  avatars: AvatarItem[];
  max?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  spacing?: number;
  gradientBorder?: boolean;
  gradientVariant?: GradientVariant;
}

const AvatarGroup = forwardRef<HTMLDivElement, AvatarGroupProps>(
  (
    {
      avatars = [],
      max = 5,
      size = 'md',
      spacing = -8,
      gradientBorder = false,
      gradientVariant = 'primary',
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant] || {
      from: theme.colors.blue[6],
      to: theme.colors.cyan[6],
    };

    const visibleAvatars = avatars.slice(0, max);
    const remainingCount = Math.max(0, avatars.length - max);
    const numericSize = typeof size === 'number' ? size : { xs: 24, sm: 32, md: 40, lg: 56, xl: 84 }[size] || 40;

    const getInitials = (name: string) => {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    };

    return (
      <Box ref={ref} style={{ display: 'flex', alignItems: 'center' }}>
        {visibleAvatars.map((avatar, index) => (
          <Tooltip key={index} label={avatar.name}>
            <Box
              style={{
                marginLeft: index === 0 ? 0 : spacing,
                zIndex: visibleAvatars.length - index,
                position: 'relative',
                padding: gradientBorder ? 2 : 0,
                borderRadius: '50%',
                background: gradientBorder
                  ? `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`
                  : 'transparent',
              }}
            >
              <MantineAvatar
                src={avatar.src}
                size={numericSize}
                radius="xl"
                color={avatar.color}
                style={{
                  border: '2px solid var(--mantine-color-body)',
                }}
              >
                {!avatar.src && getInitials(avatar.name)}
              </MantineAvatar>
            </Box>
          </Tooltip>
        ))}
        {remainingCount > 0 && (
          <Tooltip
            label={avatars
              .slice(max)
              .map((a) => a.name)
              .join(', ')}
          >
            <Box
              style={{
                marginLeft: spacing,
                zIndex: 0,
                padding: gradientBorder ? 2 : 0,
                borderRadius: '50%',
                background: gradientBorder
                  ? `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`
                  : 'transparent',
              }}
            >
              <MantineAvatar
                size={numericSize}
                radius="xl"
                color="gray"
                style={{
                  border: '2px solid var(--mantine-color-body)',
                }}
              >
                +{remainingCount}
              </MantineAvatar>
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  }
);

AvatarGroup.displayName = 'AvatarGroup';

export default AvatarGroup;
